import { useCallback, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Divider,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { enqueueSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from '../../../../../redux/store';
import axiosInstance from '../../../../../utils/axios';
import { objectToFormData } from '../../../../../api/utils';
import { updateAllCartsItemsCount, removeAllCartInfoItems } from '../../../../../redux/slices/orderSheet';
import Iconify from '../../../../../reusable-components/iconify/Iconify';
import { fToCurrency } from '../../../../../utils/formatText';
import { ConfirmDialog } from '../../../../../reusable-components/confirm-dialog';
import usePrompt from '../../../../../reusable-components/prompt/usePrompt';
import useBlocker from '../../../../../reusable-components/prompt/useBlocker';
import { CartsContext } from './_CartsContext';
import DropdownAction from '../../../../../reusable-components/dropdown-action/DropdownAction';

CartListTopBar.propTypes = {
  canViewCart: PropTypes.bool,
  canCartCheck: PropTypes.bool,
  showTableCheckbox: PropTypes.bool,
  setShowTableCheckbox: PropTypes.func,
};

export default function CartListTopBar({ canViewCart, canCartCheck, showTableCheckbox, setShowTableCheckbox }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    carts,
    setCartsWithTotals,
    totals,
    bulkDeleteItems,
    setBulkDeleteItems,
    setCheckoutCart,
    unsavedChanges,
    setUnsavedChanges,
    facilityId,
  } = useContext(CartsContext);
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const [loadingCartCheck, setLoadingCartCheck] = useState(false);
  const [savingUpdates, setSavingUpdates] = useState(false);
  const [deletingItems, setDeletingItems] = useState(false);
  const [bulkDeleteItemsWarning, setBulkDeleteItemsWarning] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  usePrompt(unsavedChanges);
  useBlocker(
    useCallback((tx) => setPendingNavigation(tx), []),
    unsavedChanges
  );

  const cartCheckFunction = () => {
    const items = carts.flatMap((cart) =>
      cart.items?.map((item) => ({
        id: item.id,
        vendorId: item.vendorId,
        itemId: item.itemId,
        quantity: item.quantity,
        price: item.price,
        vendorItemId: item.vendorItemId,
        uomType: item.uomType,
      }))
    );
    setLoadingCartCheck(true);
    axiosInstance
      .post('/CartCheck', objectToFormData({ items, facilityId, fromCart: true }))
      .then((res) => {
        carts.forEach((cart) => {
          cart.items.forEach((cartItem) => {
            cartItem.errors = res.data?.items?.find(
              (item) => cartItem.vendorItemId === item.vendorItemId && cartItem.vendorId === item.vendorId
            )?.errors;
          });
        });
        setCartsWithTotals([...carts]);
        enqueueSnackbar('Check Order Successful', { variant: 'success' });
      })
      .catch(() => enqueueSnackbar('Check Order Failed', { variant: 'error' }))
      .finally(() => setLoadingCartCheck(false));
  };

  const getOrigQuantity = () =>
    carts.flatMap((cart) => cart.items ?? []).reduce((acc, item) => acc + (item.origQty ?? item.quantity), 0);

  const bulkUpdate = () => {
    const originalCartQuantity = getOrigQuantity();
    const cartItemUpdates = carts.flatMap((cart) =>
      cart.items?.map((item) => ({
        quantity: item.quantity || 0,
        punchoutItemId: item.punchoutItemId,
        cartId: item.id,
        uomType: item.uomType,
      }))
    );
    setSavingUpdates(true);
    axiosInstance
      .post('/UpdateCart', objectToFormData({ cartItemUpdates, facilityId }))
      .then((res) => {
        dispatch(
          updateAllCartsItemsCount(
            -originalCartQuantity + cartItemUpdates.reduce((acc, item) => acc + item.quantity, 0)
          )
        );
        carts.forEach((cart) => {
          cart.items = res.data.filter((item) => item.categoryId === cart.categoryId);
        });
        setCartsWithTotals([...carts]);
        setUnsavedChanges(false);
        enqueueSnackbar('Cart updated', { variant: 'success' });
      })
      .catch(() => enqueueSnackbar('Update cart failed', { variant: 'error' }))
      .finally(() => setSavingUpdates(false));
  };

  const deleteItems = (lastItem = false) => {
    const originalCartQuantity = getOrigQuantity();
    const cartItemUpdates = carts.flatMap(
      (cart) =>
        cart.items?.map((item) => ({
          quantity:
            bulkDeleteItems[cart.categoryId] && bulkDeleteItems[cart.categoryId].includes(item.id)
              ? 0
              : item.quantity || 0,
          punchoutItemId: item.punchoutItemId,
          cartId: item.id,
          uomType: item.uomType,
        })) ?? []
    );
    setDeletingItems(true);
    axiosInstance
      .post('/UpdateCart', objectToFormData({ cartItemUpdates, facilityId }))
      .then((res) => {
        dispatch(
          updateAllCartsItemsCount(
            -originalCartQuantity + cartItemUpdates.reduce((acc, item) => acc + item.quantity, 0)
          )
        );
        if (lastItem) navigate('/dashboard/orders?filter=pending');
        else {
          carts.forEach((cart) => {
            cart.items = res.data.filter((item) => item.categoryId === cart.categoryId);
          });
          setCartsWithTotals([...carts]);
          enqueueSnackbar('Cart updated', { variant: 'success' });
        }
      })
      .catch(() => enqueueSnackbar('Cart updated failed', { variant: 'error' }))
      .finally(() => {
        setDeletingItems(false);
        setBulkDeleteItemsWarning(false);
        setShowTableCheckbox(false);
      });
  };

  const deleteCart = () => {
    //setAnchorElDel(null);
    axiosInstance
      .post(`/DeleteCart?facilityId=${facilityId}`)
      .then(() => {
        dispatch(updateAllCartsItemsCount(-getOrigQuantity()));
        dispatch(removeAllCartInfoItems());
        navigate(`/dashboard/home`);
      })
      .catch(() => enqueueSnackbar('Delete cart failed', { variant: 'error' }));
  };

  return (
    <>
      {showTableCheckbox && (
        <Box>
          <LoadingButton
            loading={deletingItems}
            color="secondary"
            onClick={() => setBulkDeleteItemsWarning(true)}
            variant="outlined"
            size="small"
          >
            Delete Items
          </LoadingButton>
          <Button
            sx={{ ml: 1 }}
            onClick={() => {
              setBulkDeleteItems(null);
              setShowTableCheckbox(false);
            }}
            variant="outlined"
            color="error"
            size="small"
          >
            Cancel
          </Button>

          <Dialog
            fullWidth
            maxWidth="xs"
            open={bulkDeleteItemsWarning}
            onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        setBulkDeleteItemsWarning(false);
      }}
          >
            <DialogTitle sx={{ pb: 2 }}>Delete all items?</DialogTitle>
            <DialogContent sx={{ typography: 'body2' }}>
              Are you sure want to delete all items selected?
              {!carts?.some((cart) =>
                cart?.items?.some(
                  (item) => bulkDeleteItems[cart.categoryId] && !bulkDeleteItems[cart.categoryId].includes(item.id)
                )
              ) && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  This will remove your cart.
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <LoadingButton
                variant="contained"
                color="error"
                loading={deletingItems}
                onClick={() =>
                  deleteItems(
                    !carts?.some((cart) =>
                      cart?.items?.some(
                        (item) =>
                          bulkDeleteItems[cart.categoryId] && !bulkDeleteItems[cart.categoryId].includes(item.id)
                      )
                    )
                  )
                }
              >
                Delete Selected Items
              </LoadingButton>
              <Button variant="outlined" color="inherit" onClick={() => setBulkDeleteItemsWarning(false)}>
                Cancel
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}
      {canViewCart && !showTableCheckbox && (
        <Grid container spacing={1.5}>
          {canCartCheck && (
            <Grid item>
              <Tooltip title="Check order for issues" arrow placement="top">
                <LoadingButton
                  variant="outlined"
                  size="small"
                  color="info"
                  startIcon={<Iconify icon="mdi:cart-check" />}
                  onClick={cartCheckFunction}
                  loading={loadingCartCheck}
                >
                  Check Order
                </LoadingButton>
              </Tooltip>
            </Grid>
          )}
          <Grid item>
            <Button
              sx={{ ml: 0, height: 1, minWidth: 0, px: 1 }}
              variant="outlined"
              size="small"
              color="secondary"
              onClick={(e) => setActionsAnchorEl(e.currentTarget)}
            >
              <Iconify icon="mdi:more-vert" />
            </Button>
          </Grid>
          <Menu
            anchorEl={actionsAnchorEl}
            open={Boolean(actionsAnchorEl)}
            onClose={() => setActionsAnchorEl(null)}
            slotProps={{
              paper: {
                sx: {
                  width: 245,
                  p: 1,
                  borderRadius: '5px',
                  boxShadow: '0px 2px 6px rgba(0,0,0,.06), 0px 8px 24px rgba(0,0,0,.12)',
                },
              },
              list: {
                sx: {
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '14px',
                  p: 0,
                },
              },
            }}
          >
            <MenuItem
              disableRipple
              disableGutters
              sx={{ p: 0, minHeight: 'unset' }}
              onClick={() => {
                setActionsAnchorEl(null);
                window.open(`Api/DownloadCartPDF?facilityId=${facilityId}`, '_blank');
              }}
            >
              <DropdownAction badgeIcon="material-symbols:download">Download PDF</DropdownAction>
            </MenuItem>

            <MenuItem
              disableRipple
              disableGutters
              sx={{ p: 0, minHeight: 'unset' }}
              onClick={() => {
                setActionsAnchorEl(null);
                window.open(`Api/DownloadCart?facilityId=${facilityId}`, '_blank');
              }}
            >
              <DropdownAction badgeIcon="material-symbols:download">Download Excel</DropdownAction>
            </MenuItem>

            <Divider sx={{ my: 0.5, mx: 2 }} />

            <MenuItem
              disableRipple
              disableGutters
              sx={{ p: 0, minHeight: 'unset' }}
              onClick={() => {
                setBulkDeleteItems({});
                setActionsAnchorEl(null);
                setShowTableCheckbox(true);
              }}
            >
              <DropdownAction badgeIcon="mdi:delete-sweep-outline">Delete Multiple Items</DropdownAction>
            </MenuItem>

            <MenuItem disableRipple disableGutters sx={{ p: 0, minHeight: 'unset' }}>
              <DropdownAction
                buttonComponent={ConfirmDialog}
                badgeIcon="mdi:delete-outline"
                iconColor="error.main"
                iconBgColor="#FDEDED"
                textColor="error.main"
                button="text"
                title="Delete all carts?"
                color="error"
                content="Doing so will delete all items in all carts. Are you sure you want to delete all carts?"
                actionButton="Delete all carts"
                action={deleteCart}
                icon="delete-outline"
                tooltip="Delete all carts"
              >
                Delete all Carts
              </DropdownAction>
            </MenuItem>
          </Menu>
          {unsavedChanges && (
            <Grid item>
              <LoadingButton
                variant="outlined"
                size="small"
                startIcon={<Iconify icon="tabler:refresh" />}
                onClick={bulkUpdate}
                loading={savingUpdates}
              >
                Save Cart Changes
              </LoadingButton>
            </Grid>
          )}

          <Grid item>
            <Tooltip arrow={"top"} title={unsavedChanges ? 'There are unsaved changes in your cart. Please save before checkout.' : ''}>
              <span>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setCheckoutCart(-1)}
                  disabled={unsavedChanges || !carts.some((cart) => cart.orderable && cart.items?.length > 0)}
                >
                  Proceed to Checkout{' '}
                  {totals?.mainTotal.items
                    ? `(${totals?.mainTotal.items} items - ${fToCurrency(totals?.mainTotal.price ?? 0)})`
                    : ''}
                </Button>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      )}

      <Dialog
        open={!!pendingNavigation}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          setPendingNavigation(null);
        }}
        PaperProps={{
          sx: {
            width: '550px',
            maxWidth: '550px',
            minHeight: '193px',
            p: '24px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: '10px',
            gap: '48px',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '15.8px', width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
            <Iconify icon="eva:alert-circle-outline" width={24} height={24} sx={{ flexShrink: 0 }} />
            <Box sx={{ flexGrow: 1, typography: 'h6' }}>You have unsaved changes</Box>
            <IconButton size="small" onClick={() => setPendingNavigation(null)} sx={{ mr: -0.5 }}>
              <Iconify icon="mdi:close" />
            </IconButton>
          </Box>
          <Box sx={{ typography: 'body2' }}>Your changes will be lost if you leave this page without saving.</Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => {
                pendingNavigation?.retry();
                setPendingNavigation(null);
              }}
            >
              Leave Anyway
            </Button>
            <LoadingButton
              variant="contained"
              color="primary"
              loading={savingUpdates}
              onClick={() => {
                setPendingNavigation(null);
                bulkUpdate();
              }}
            >
              Save Changes
            </LoadingButton>
          </Box>
        </Box>
      </Dialog>
    </>
  );
}
